/**
 * Get size in bytes of a base64 string (optionally with data URL prefix).
 * @param {string} base64String
 * @returns {number}
 */
export function base64Size(base64String) {
  const base64 = base64String.split(",")[1] || base64String;
  return (
    Math.ceil((base64.length * 3) / 4) - (base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0)
  );
}

/**
 * Yield to the event loop so the UI stays responsive during compression.
 * @returns {Promise<void>}
 */
function yieldToMain() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    } else {
      setTimeout(resolve, 0);
    }
  });
}

/**
 * Compress a base64 image (data URL) by reducing JPEG quality until under target size.
 * Uses async iterations so the main thread is not blocked and the page stays responsive.
 * @param {string} base64
 * @param {number} targetSizeBytes
 * @returns {Promise<string>}
 */
export function compressBase64ToSize(base64, targetSizeBytes) {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = function () {
      (async function runCompression() {
        await yieldToMain();

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(base64);
          return;
        }
        ctx.drawImage(img, 0, 0, img.width, img.height);

        const minQuality = 0.5;
        const step = 0.05;
        let quality = 0.95;
        let compressedBase64 = canvas.toDataURL("image/jpeg", quality);

        if (base64Size(compressedBase64) <= targetSizeBytes) {
          resolve(compressedBase64);
          return;
        }

        while (quality > minQuality) {
          await yieldToMain();
          quality -= step;
          compressedBase64 = canvas.toDataURL("image/jpeg", quality);
          if (base64Size(compressedBase64) <= targetSizeBytes) {
            resolve(compressedBase64);
            return;
          }
        }
        resolve(compressedBase64);
      })();
    };
    img.src = base64;
  });
}

export const compressImageWithCanvas = (file, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Resize if needed (e.g., max 1920px)
        const maxDim = 1920;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Export as WebP
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject("Compression failed");
            resolve(new File([blob], file.name.split(".")[0] + ".webp", { type: "image/webp" }));
          },
          "image/webp",
          quality
        );
      };

      img.onerror = reject;
    };
  });
};
